import { getTableColumns } from "drizzle-orm";
import { index, pgEnum, pgTable, text, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import { householdMemberDocumentsTable } from "./householdMemberDocumentsSchema";
import { patientsTable } from "./patientsSchema";
import { customTimestamp } from "./utils/types";

const documentStatusEnumSchema = z.enum(["PROCESSING", "UPLOADED", "FAILED"]);
export type DocumentStatusEnumType = z.infer<typeof documentStatusEnumSchema>;

export const documentStatusEnum = pgEnum(
  "document_status_enum",
  documentStatusEnumSchema.enum,
);

export const patientUploadedFilesTable = pgTable(
  "patient_uploaded_files",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    storageKey: text("storage_key").unique().notNull(),
    patientId: uuid("patientId")
      .notNull()
      .references(() => patientsTable.id, { onDelete: "cascade" }),
    householdMemberDocumentId: uuid("household_member_document_id")
      .notNull()
      .references(() => householdMemberDocumentsTable.id, {
        onDelete: "cascade",
      }),
    status: documentStatusEnum("status").notNull().default("PROCESSING"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("patient_uploaded_documents_patient_id_index").on(table.patientId),
    index("patient_uploaded_documents_household_member_document_id_index").on(
      table.householdMemberDocumentId,
    ),
  ],
);

export const patientUploadedFilesTableColumns = getTableColumns(
  patientUploadedFilesTable,
);

export const patientUploadedFileSchema = createSelectSchema(
  patientUploadedFilesTable,
  {
    status: documentStatusEnumSchema,
  },
);
export type PatientUploadedDocumentType = z.infer<
  typeof patientUploadedFileSchema
>;

export const patientUploadedDocumentInsertSchema = createInsertSchema(
  patientUploadedFilesTable,
  {
    patientId: z.uuid(),
    householdMemberDocumentId: z.uuid(),
    status: documentStatusEnumSchema,
  },
).omit({
  createdAt: true,
  updatedAt: true,
});
export type PatientUploadedDocumentInsertType = z.infer<
  typeof patientUploadedDocumentInsertSchema
>;
